/**
 * loading.tsx — the one loading look: the accent spinner, and under it
 * "Loading the game". The caller centres it in whatever waits: the main
 * column while the join's snapshot comes in (game-screen.tsx), the globe's
 * place while its engine starts — there quiet — and, while the game's own code
 * is on its way after the way in, the page (`LoadingPage`, app.tsx).
 *
 * The spinner is the element's whole box and the line hangs under it, so the
 * spinner sits at the same centre with the line or without it: a wait that
 * hands over to another never moves it. `quiet` draws no line. What is drawn
 * is for the eye alone. One voice says the wait (`LoadingVoice`): a status
 * line the app keeps on the page from the start (ui/status-line.tsx), which
 * says "Loading the game" while any loading look is on the page and nothing
 * once none is. Each look holds it while it is mounted, and the last to leave
 * lets it go a fifth of a second later (HANDOVER_MS), so a look that comes as
 * another leaves — the page's as the game screen's comes, the game screen's as
 * the globe's does, in one commit or a render later — finds it still held: a
 * wait handed over is one wait, said once. The page and the game screen had a
 * line each, and said it twice. Letting the words go a moment late says
 * nothing: words taken out of a polite region are not announced. It says
 * nothing more however long the wait runs (the user's call at group g's
 * sign-off, X3). Before any of this, index.html's boot frame paints the ground
 * and the plane mark.
 */

import { useEffect, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from '../ui/spinner';
import { StatusLine } from '../ui/status-line';
import styles from './loading.module.css';

/** How long the voice is held after the last look leaves, for a look that comes as it leaves. */
const HANDOVER_MS = 200;

/** How many loading looks are on the page; whether the voice is held; the timer that lets it go; what hears it change. */
let looks = 0;
let held = false;
let letGo: ReturnType<typeof setTimeout> | undefined;
const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function hold(next: boolean) {
  if (held === next) return;
  held = next;
  for (const listener of listeners) listener();
}

/** Holds the one voice while a look is on the page, and lets it go HANDOVER_MS after the last look leaves. */
function holdVoice() {
  looks += 1;
  clearTimeout(letGo);
  hold(true);
  return () => {
    looks -= 1;
    if (looks === 0) letGo = setTimeout(() => hold(false), HANDOVER_MS);
  };
}

export function Loading({ quiet = false }: { quiet?: boolean }) {
  const { t } = useTranslation();
  useEffect(holdVoice, []);
  return (
    <div className={styles.loading}>
      <Spinner size="lg" />
      {!quiet && (
        <p className={styles.line} aria-hidden="true">
          {t('loading.game')}
        </p>
      )}
    </div>
  );
}

/**
 * The one voice of every wait: says "Loading the game" while a loading look is
 * on the page, and nothing once none has been for HANDOVER_MS. Mounted once,
 * where it stays — the app's root, before any wait begins — so the words come
 * as a change.
 */
export function LoadingVoice() {
  const { t } = useTranslation();
  const waiting = useSyncExternalStore(subscribe, () => held);
  return <StatusLine>{waiting ? t('loading.game') : ''}</StatusLine>;
}

/**
 * The page while the game's code is on its way, between the way in and the
 * game screen: the ground, the rail's strip where the rail will stand, empty,
 * and the loading look centred beside it — where the game screen's own wait
 * puts it, so the spinner does not move as the shell fills in around it.
 */
export function LoadingPage() {
  return (
    <div className={styles.page}>
      <div className={styles.railStrip} data-rail-place />
      <div className={styles.beside}>
        <Loading />
      </div>
    </div>
  );
}
